import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScrollEdges {
  /** Content is hidden above the visible area. */
  top: boolean;
  /** Content is hidden below the visible area. */
  bottom: boolean;
}

export interface HorizontalScrollEdges {
  /** Content is hidden to the left of the visible area. */
  left: boolean;
  /** Content is hidden to the right of the visible area. */
  right: boolean;
}

interface Geometry { scrollTop: number; clientHeight: number; scrollHeight: number }
interface HorizontalGeometry { scrollLeft: number; clientWidth: number; scrollWidth: number }

const NO_SIDES: HorizontalScrollEdges = { left: false, right: false };

const NONE: ScrollEdges = { top: false, bottom: false };
/** Sub-pixel rounding (zoom, fractional heights) must not leave a fade stuck on. */
const TOLERANCE = 2;

export function computeScrollEdges({ scrollTop, clientHeight, scrollHeight }: Geometry): ScrollEdges {
  if (scrollHeight - clientHeight <= TOLERANCE) return NONE;
  return {
    top: scrollTop > TOLERANCE,
    bottom: scrollTop + clientHeight < scrollHeight - TOLERANCE,
  };
}

/** The same test for a box that scrolls sideways (a board of columns): which side still has content hidden. */
export function computeHorizontalScrollEdges({ scrollLeft, clientWidth, scrollWidth }: HorizontalGeometry): HorizontalScrollEdges {
  if (scrollWidth - clientWidth <= TOLERANCE) return NO_SIDES;
  return {
    // scrollLeft is negative in a right-to-left box; its size is what counts.
    left: Math.abs(scrollLeft) > TOLERANCE,
    right: Math.abs(scrollLeft) + clientWidth < scrollWidth - TOLERANCE,
  };
}

/**
 * Which edges of a scroll container have content hidden beyond them. Drives the
 * fade at the top and bottom of a menu that is taller than its box. Re-measured
 * on scroll, when the box or its children change size (a section collapses) and
 * when children are added or removed.
 */
export function useScrollEdges<T extends HTMLElement = HTMLElement>() {
  return useMeasuredEdges<T, ScrollEdges>(computeScrollEdges, NONE, (a, b) => a.top === b.top && a.bottom === b.bottom);
}

/** useScrollEdges for a box that scrolls sideways: `edges.left` / `edges.right`. */
export function useHorizontalScrollEdges<T extends HTMLElement = HTMLElement>() {
  return useMeasuredEdges<T, HorizontalScrollEdges>(computeHorizontalScrollEdges, NO_SIDES, (a, b) => a.left === b.left && a.right === b.right);
}

function useMeasuredEdges<T extends HTMLElement, E>(
  compute: (el: T) => E,
  none: E,
  same: (a: E, b: E) => boolean,
) {
  const ref = useRef<T | null>(null);
  const [edges, setEdges] = useState<E>(none);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = compute(el);
    setEdges(prev => (same(prev, next) ? prev : next));
    // compute and same are module-level functions, passed fresh each render but never different.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(el);
      Array.from(el.children).forEach(child => resizeObserver?.observe(child));
    }
    let mutationObserver: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        resizeObserver?.disconnect();
        if (resizeObserver) {
          resizeObserver.observe(el);
          Array.from(el.children).forEach(child => resizeObserver?.observe(child));
        }
        measure();
      });
      mutationObserver.observe(el, { childList: true, subtree: true });
    }
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [measure]);

  return { ref, edges, measure };
}
